import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, IconButton, Typography, Slide, useTheme, CircularProgress } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import ReactMarkdown from 'react-markdown';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

interface AboutModalProps {
  open: boolean;
  onClose: () => void;
}

export const AboutModal: React.FC<AboutModalProps> = ({ open, onClose }) => {
  const [readmeContent, setReadmeContent] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (open) {
      setLoading(true);
      fetch('/md-files/README.md')
        .then(res => res.text())
        .then(content => {
          setReadmeContent(content);
          setLoading(false);
        })
        .catch(() => {
          setReadmeContent('# About\nFailed to load content.');
          setLoading(false);
        });
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          maxHeight: '85vh'
        }
      }}
    >
      <Box sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}10 100%)`,
        p: 4,
        position: 'relative'
      }}>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            position: 'absolute', 
            right: 12, 
            top: 12,
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { bgcolor: 'white', transform: 'rotate(90deg)' },
            transition: 'all 0.3s'
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              bgcolor: theme.palette.primary.main, 
              borderRadius: 2, 
              p: 1.5,
              display: 'flex',
              boxShadow: `0 4px 12px ${theme.palette.primary.main}40`
            }}>
              <InfoIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>About</Typography>
              <Typography variant="body2" color="text.secondary">
                Learn more about COE Marketplace
              </Typography>
            </Box>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ 
              fontSize: '15px', 
              lineHeight: 1.8,
              bgcolor: 'white',
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              maxHeight: '60vh',
              overflowY: 'auto',
              '& h1': { fontSize: '1.8rem', fontWeight: 700, mb: 2, color: theme.palette.primary.main },
              '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 3, mb: 1.5 },
              '& h3': { fontSize: '1.2rem', fontWeight: 600, mt: 2, mb: 1 },
              '& p': { mb: 1.5 },
              '& ul, & ol': { pl: 3, mb: 2 },
              '& li': { mb: 0.5 },
              '& code': { 
                bgcolor: `${theme.palette.primary.main}10`, 
                px: 0.8, 
                py: 0.3, 
                borderRadius: 0.5,
                fontSize: '0.9em'
              },
              '& hr': { my: 2, borderColor: 'divider' }
            }}>
              <ReactMarkdown>{readmeContent}</ReactMarkdown>
            </Box>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};
